/**
 * PROC-03 Analogical scaffold density (INV-PACK-03; change C-PACK-REG).
 *
 * "the way X does Y" maps behaviour to behaviour without a concrete image. Events: every "the way"
 * that is not a non-comparative construction ("the way to / of / in which / out / back / home /
 * forward"), per 1k prose words (the pack subtracted the non-simile count from the total; here the
 * non-simile uses simply are not events). "the way" split by a soft line break inside a paragraph
 * still counts (whitespace, not a literal space), so hard-wrapped and reflowed variants agree.
 * Prose register only (C-PACK-REG: the pack read the whole text, quotes and headings included).
 * The concrete-image count ("like a/an/the") is reported as a secondary feature and summary line.
 * Ramp: the pack's `[humanCeiling 1.0 → 0, machineFloor 2.5 → 100]`. Weight 1.5 × 0.75.
 */
import { safeScan } from '../lexicons/regex-list.ts';
import { line, nativeMeasure, proseBlocks, rateDef, wordExposure } from './a-shared.ts';
import { makeEvidence, rateChannel } from './framework.ts';
import type { ChannelImpl, Measurement, RunContext } from './types.ts';

const THE_WAY = /\bthe\s+way\b(?!\s+(?:to|of|in\s+which|out|back|home|forward)\b)/gi;
const LIKE_CONCRETE = /\blike\s+(?:a|an|the)\b/gi;

export const proc03Def = rateDef({
	id: 'PROC-03',
	name: 'Analogical scaffold density',
	family: 'FIGURE',
	features: [{ id: 'theWay', label: '"the way …" simile' }],
	oneWay: true,
	minExposure: 1500,
	exposureUnit: 'words',
	lang: 'native',
	ramp: [
		[0, 0],
		[1, 0],
		[2.5, 100],
	],
	unitScale: 1000,
	unitLabel: '"the way" similes per 1k words',
	phi0: 1.5,
	weight: 1.5 * 0.75,
	nd: 0.15,
	changes: ['C-PACK-REG'],
});

function measure(ctx: RunContext): Measurement {
	const { doc } = ctx;
	const k = new Float64Array(doc.paragraphs.count);
	const e = wordExposure(doc);
	const ev = makeEvidence();
	let theWay = 0;
	let concrete = 0;
	for (const b of proseBlocks(doc)) {
		if (/the\s+way/i.test(b.text)) {
			safeScan(THE_WAY, b.text, b.base, (s, en) => {
				theWay++;
				k[b.p] = (k[b.p] as number) + 1;
				ev.add(s, en, 0, 1);
			});
		}
		if (/like\s/i.test(b.text)) safeScan(LIKE_CONCRETE, b.text, b.base, () => concrete++);
	}
	const ratio = concrete ? theWay / concrete : null;
	return {
		k,
		e,
		evidence: ev,
		summary: [
			`"the way ..." x${theWay}`,
			`"like a/an/the" x${concrete}`,
			line(ratio ? `ratio=${ratio.toFixed(2)}:1` : 'ratio=n/a', 'abstract vs concrete simile'),
		],
		features: { theWay, likeConcrete: concrete },
	};
}

export const proc03: ChannelImpl = rateChannel(proc03Def, nativeMeasure(measure));
export { proc03Def as def, proc03 as impl };
